import { InfoOutlined, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material"
import {
  Box,
  Button,
  Stack,
  TableCell,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
  useTheme,
  Skeleton,
  Collapse,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material"
import { Paper } from "@mui/material"
import { DialogTitle } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { useActiveAddress } from "arweave-wallet-kit"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  Area,
  AreaChart,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts"

import { DepositForm } from "./DepositForm"
import {
  emergencyExit,
  getPortfolioAgents,
  HoldingEntry,
  PortfolioAgent,
  PortfolioAgentStatus,
  readAgentInfo,
  readExitPnl,
  togglePause,
  withdraw,
} from "@/api/portfolio-agents-api"
import { useTokenPrices } from "@/api/portfolio-api"
import { DexiSpinner } from "@/components/DexiSpinner"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { IdBlock } from "@/components/IdBlock"
import { InMemoryTable } from "@/components/InMemoryTable"
import { LoadingSkeletons } from "@/components/LoadingSkeletons"

import { NumberFont } from "@/components/RootLayout/fonts"
import { TokenAmountBlock } from "@/components/TokenAmountBlock"
import { TokenAvatar } from "@/components/TokenAvatar"
import { UsdAmountBlock } from "@/components/UsdAmountBlock"
import ShineBorder from "@/components/shine-border"
import { useTokenInfo, useTokenInfos } from "@/hooks/use-token-info"
import { truncateId } from "@/utils/data-utils"
import {
  formatNumberAuto,
  formatRelative,
  formatDate,
  formatFullDate,
  parseBigIntAsNumber,
  formatDuration,
} from "@/utils/format"

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#AA336A", "#33AA66", "#AA6633"]

const STATUS_LABEL_MAP: Record<PortfolioAgentStatus, string> = {
  Paused: "Paused",
  Rebalancing: "Rebalancing",
  Liquidating: "Retiring",
  Retired: "Retired",
  EmergencyExiting: "Retiring",
  EmergencyExited: "Retired",
  AwaitingSubscriptions: "Initializing",
  AwaitingInitialDeposit: "Awaiting Top-up",
  AwaitingStrategyConfiguration: "Awaiting Strategy Configuration",
  Ready: "Ready",
  Deactivated: "Retired",
  Withdrawing: "Withdrawing",
}

const STATUS_COLOR_MAP: Record<PortfolioAgentStatus, string> = {
  Paused: "var(--mui-palette-warning-main)",
  Rebalancing: "var(--mui-palette-warning-main)",
  Liquidating: "var(--mui-palette-warning-main)",
  Retired: "var(--mui-palette-secondary-main)",
  EmergencyExiting: "var(--mui-palette-warning-main)",
  EmergencyExited: "var(--mui-palette-secondary-main)",
  AwaitingSubscriptions: "var(--mui-palette-warning-main)",
  AwaitingInitialDeposit: "var(--mui-palette-warning-main)",
  AwaitingStrategyConfiguration: "var(--mui-palette-warning-main)",
  Ready: "var(--mui-palette-accent-main)",
  Deactivated: "var(--mui-palette-secondary-main)",
  Withdrawing: "var(--mui-palette-secondary-main)",
}

const PENDING_STATUSES: PortfolioAgentStatus[] = [
  "Rebalancing",
  "Liquidating",
  "EmergencyExiting",
  "AwaitingSubscriptions",
  "AwaitingStrategyConfiguration",
  "Withdrawing",
]

export function EntityPortfolioAgents(props: {
  entityId: string
  onPortfolioAgentsValue?: (value: number | null) => void
}) {
  const { entityId, onPortfolioAgentsValue } = props

  const { data: agents } = useQuery({
    queryKey: [`active_portfolio_agents`, entityId],
    enabled: !!entityId,
    queryFn: async () => {
      if (!entityId) throw new Error("Params missing")

      const result = await getPortfolioAgents(entityId)
      console.log("onPortfolioAgents", result)
      return result
    },
  })

  const [value, setValue] = useState<Record<string, number>>()

  const totalValue = useMemo(() => {
    if (!agents) return null

    const val = Object.values(value || {}).reduce((acc, x) => {
      return acc + x
    }, 0)

    onPortfolioAgentsValue?.(val)

    return val
  }, [agents, value, onPortfolioAgentsValue])

  useEffect(() => {
    setValue({})
  }, [entityId])

  return (
    <Paper sx={{ border: 0, paddingX: 2, paddingY: 2 }}>
      <Typography
        sx={{
          marginBottom: 1,
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
        }}
        variant="h4"
      >
        Portfolio Agents{" "}
        {!!totalValue && (
          <>
            •{" "}
            <Typography fontFamily={NumberFont} variant="inherit" component="span">
              <UsdAmountBlock amount={totalValue} />
            </Typography>
          </>
        )}
      </Typography>
      {!agents ? (
        <LoadingSkeletons />
      ) : (
        <Box>
          <InMemoryTable
            // sx={{ borderRadius: 2, overflow: "hidden" }}
            initialSortField="value"
            initialSortDir="desc"
            data={agents}
            headerCells={[
              { label: "#", align: "right", sx: { width: 50 } },
              { label: "Agent" },
              { label: "Tokens", align: "right" },
              { label: "Status", align: "right" },
              { label: "Net worth", align: "right" },
              {
                label: (
                  <Stack direction="row" gap={0.5} alignItems="center" justifyContent="flex-end">
                    Current max. imbalance
                    <MuiTooltip title="Displays the largest deviation from the target allocation currently present in your portfolio. This percentage is relative to each token’s target share, rather than the entire portfolio.">
                      <InfoOutlined fontSize="small" />
                    </MuiTooltip>
                  </Stack>
                ),
                align: "right",
              },
              { label: "Last 7 Days", align: "right", sx: { width: 200 } },
            ]}
            renderRow={(agent: PortfolioAgent, index) => (
              <ErrorBoundary key={agent.id} fallback={<>Something went wrong</>}>
                <AgentRow
                  agent={agent}
                  index={index}
                  onAgentValue={(value: number) => {
                    setValue((prev) => ({ ...prev, [agent.id]: value }))
                  }}
                />
              </ErrorBoundary>
            )}
          />
        </Box>
      )}
    </Paper>
  )
}

type AgentData = {
  quote_token: string
  weights: string[]
  status: string
  token_processes: string[]
  amm_processes: string[]
  portfolio_owner: string
  created_at_ts: number
  id: string
}

type AgentRowProps = {
  agent: AgentData
  onAgentValue?: (value: number) => void
  singleAgentView?: boolean
  index?: number
}

export function AgentRow(props: AgentRowProps) {
  const { index = 0, agent, onAgentValue, singleAgentView = false } = props

  const { data: agentInfo, refetch: fetchAgentInfo } = useQuery({
    queryKey: ["agent_info", agent.id],
    queryFn: async () => {
      // console.log("Fetching Agent Info", agent.id)
      const res = await readAgentInfo(agent.id)
      console.log("onAgentInfo", agent.id, res)
      return res
    },
    enabled: !!agent.id,
  })

  const [tokenInfos] = useTokenInfos(agent.token_processes)
  const [quoteTokenInfo] = useTokenInfo(agent.quote_token)

  const [tokenPrices] = useTokenPrices()

  const quoteTokenPriceUSD = useMemo(() => {
    if (!quoteTokenInfo || !tokenPrices || !tokenPrices[quoteTokenInfo.id]?.price) {
      return null
    }
    console.log("onQuoteTokenPriceUSD", tokenPrices[quoteTokenInfo.id].price)
    return tokenPrices[quoteTokenInfo.id].price
  }, [tokenPrices, quoteTokenInfo])

  const { data: agentExitPnl } = useQuery({
    queryKey: ["agent_exit_pnl", agent.id, quoteTokenPriceUSD],
    queryFn: async () => {
      const res = await readExitPnl(agent.id, quoteTokenPriceUSD!)
      console.log("onAgentExitPnl", agent.id, res)
      return res
    },
    enabled: !!agent.id && quoteTokenPriceUSD !== null,
  })

  const netWorth = useMemo(() => {
    if (!agentInfo || !tokenPrices || !tokenInfos || !quoteTokenInfo) return null
    if (!agentInfo.Holdings || !agentInfo.Holdings.entries) return 0

    // let totalValue = 0
    // agentInfo.Holdings.entries.forEach((entry) => {
    //   // Find the token info from tokenInfos or quoteTokenInfo
    //   const tokenInfo =
    //     tokenInfos.find((info) => info.id === entry.token) ||
    //     (entry.token === quoteTokenInfo.id ? quoteTokenInfo : undefined)
    //   if (!tokenInfo) return

    //   const tokenPrice = tokenPrices[tokenInfo.id]?.price || 0
    //   console.log("HOLDING VALUE (qt) ", entry.amount)
    //   const amount = parseFloat(parseBigIntAsNumber(entry.amount, tokenInfo.denomination))

    //   totalValue += tokenPrice * amount
    // })

    // ALTERNATIVE CALCULATION - for simplicity, but let's discuss if this is acceptable
    // use the total net value (in quote token) directly from agentInfo, since it is tracked

    const totalValueQuoteToken = parseFloat(agentInfo.Holdings.realTotalValue)
    const totalValueQuoteTokenDecimalForm =
      totalValueQuoteToken / Math.pow(10, quoteTokenInfo.denomination)
    const quoteTokenPriceInfo = tokenPrices[quoteTokenInfo.id]
    if (!quoteTokenPriceInfo || !quoteTokenPriceInfo.price) {
      return 0
    }
    const quoteTokenPriceUSD = quoteTokenPriceInfo.price
    const totalValue = totalValueQuoteTokenDecimalForm * quoteTokenPriceUSD

    if (typeof totalValue === "number" && !isNaN(totalValue)) {
      onAgentValue?.(totalValue)
    }
    return totalValue
  }, [agentInfo, tokenInfos, tokenPrices, quoteTokenInfo])

  const totalDeposited = useMemo(() => {
    if (!agentInfo || !tokenPrices || !quoteTokenInfo) return null

    let total = BigInt(0)

    try {
      for (const deposit of agentInfo.Deposits) {
        total += BigInt(deposit.usdValue)
      }
    } catch {}

    return parseFloat(parseBigIntAsNumber(total, quoteTokenInfo.denomination))
  }, [agentInfo, tokenPrices, quoteTokenInfo])

  const pnl = useMemo(() => {
    if (!agentInfo || !quoteTokenInfo || !agentExitPnl) return null

    if (agent.created_at_ts >= 1739200020000) {
      return Number(parseBigIntAsNumber(agentExitPnl.absPnlAllTime, quoteTokenInfo.denomination))
    }

    if (!tokenPrices) return null

    const price = tokenPrices[quoteTokenInfo.id].price

    return (
      Number(parseBigIntAsNumber(agentExitPnl.absPnlAllTime, quoteTokenInfo.denomination)) * price
    )
  }, [agentInfo, tokenPrices, quoteTokenInfo, agentExitPnl])

  // const totalWithdrawals = useMemo(() => {
  //   if (!agentInfo || !tokenPrices || !quoteTokenInfo) return null

  //   let total = BigInt(0)

  //   for (const withdrawal of agentInfo.Withdrawals) {
  //     total += BigInt(withdrawal.amount)
  //   }

  //   const totalUsd =
  //     parseFloat(parseBigIntAsNumber(total, quoteTokenInfo.denomination)) *
  //     tokenPrices[quoteTokenInfo.id].price

  //   return totalUsd
  // }, [agentInfo, tokenPrices, quoteTokenInfo])

  const pieData = useMemo(() => {
    if (
      !agentInfo ||
      !agentInfo.Holdings ||
      !agentInfo.Holdings.entries ||
      agentInfo.Status === "EmergencyExited" ||
      agentInfo.Status === "Retired" ||
      agentInfo.Status === "Deactivated"
    )
      return []

    const data = agentInfo.Holdings.entries.map((entry) => ({
      name: entry.ticker,
      value: Number(entry.realActualPercent) / 100,
    }))

    if (data.length === agentInfo.PortfolioTokens.length) {
      // we are missing the quote token => insert artificially here
      data.unshift({
        name: agentInfo["Quote-Token-Ticker"],
        value: 0,
      })
    }

    return data
  }, [agentInfo])

  const targetPieData = useMemo(() => {
    if (!agentInfo || !agentInfo.PortfolioTokens) return []

    const data = agentInfo.PortfolioTokens.map((entry) => ({
      name: entry.ticker,
      value: entry.weight / 100,
    }))

    if (data.length === agentInfo.PortfolioTokens.length) {
      // we are missing the quote token => insert artificially here
      data.unshift({
        name: agentInfo["Quote-Token-Ticker"],
        value: 0,
      })
    }

    return data
  }, [agentInfo])

  // compute the total networth of the past 7 days
  const last7DaysData = useMemo(() => {
    if (!agentInfo?.DailyHistory || !quoteTokenInfo || !tokenPrices) return []

    const data: any[] = []

    agentInfo.DailyHistory.slice(-6).forEach((historyItem) => {
      data.push({
        timestamp: historyItem.timestamp,
        value:
          Number(historyItem.holdings.realTotalValueUSD) /
          Math.pow(10, quoteTokenInfo.denomination),
      })
    })

    // current networth
    data.push({
      timestamp: Date.now(),
      value: netWorth,
    })

    if (data.length !== 7) {
      // fill with 0s if we have less than 7 days
      const missingDays = 7 - data.length
      for (let i = 0; i < missingDays; i++) {
        data.unshift({
          timestamp: Date.now() - (missingDays - i) * 86400 * 1000,
          value: 0,
        })
      }
    }

    return data
  }, [agentInfo, quoteTokenInfo, netWorth])

  const [chartData, tickers] = useMemo(() => {
    if (!agentInfo?.DailyHistory || !quoteTokenInfo || !tokenPrices) return [[], []]

    const quoteTokenPriceInfo = tokenPrices[quoteTokenInfo.id]
    if (!quoteTokenPriceInfo?.price) return [[], []]

    const tickersSet = new Set<string>()
    tickersSet.add(quoteTokenInfo.ticker)
    agentInfo.PortfolioTokens.forEach((token) => tickersSet.add(token.ticker))
    const tickers = Array.from(tickersSet)

    const data: any[] = []

    const firstDeposit = agentInfo.Deposits[0]
    if (firstDeposit) {
      // start from 0
      data.push({
        timestamp: firstDeposit.timestamp,
        ...Object.fromEntries(tickers.map((t) => [t, 0])),
      })

      // show the first deposit
      data.push({
        timestamp: firstDeposit.timestamp,
        ...Object.fromEntries(tickers.map((t) => [t, 0])),
        [quoteTokenInfo.ticker]:
          parseFloat(firstDeposit.usdValue) / Math.pow(10, quoteTokenInfo.denomination),
      })
    }

    agentInfo.DailyHistory.forEach((historyItem) => {
      const { holdings, timestamp, numDeposits, numRebalancings } = historyItem
      if (!holdings?.entries) return

      const dataPoint: any = { timestamp, numDeposits, numRebalancings }
      tickers.forEach((ticker) => {
        dataPoint[ticker] = 0
      })

      holdings.entries.forEach((entry) => {
        if (entry.realNetValueUSD) {
          const value = parseInt(entry.realNetValueUSD) / Math.pow(10, quoteTokenInfo.denomination)
          dataPoint[entry.ticker] = value
        }
      })

      data.push(dataPoint)
    })

    const currentHoldings: any = {
      timestamp: Date.now(),
      [quoteTokenInfo.ticker]: 0,
    }
    const quoteTokenPriceUSD = quoteTokenPriceInfo.price
    agentInfo.Holdings.entries.forEach((entry) => {
      const value = parseInt(entry.realNetValue) / Math.pow(10, quoteTokenInfo.denomination)
      currentHoldings[entry.ticker] = value * quoteTokenPriceUSD
    })
    data.push(currentHoldings)

    const lastWithdrawal: any = {
      timestamp: Date.now(),
      [quoteTokenInfo.ticker]: 0,
    }

    if (agentInfo.Withdrawals.length > 0) {
      agentInfo.PortfolioTokens.forEach((entry) => {
        lastWithdrawal[entry.ticker] = 0
      })
      data.push(lastWithdrawal)
    }

    return [data, tickers]
  }, [agentInfo, quoteTokenInfo, tokenPrices])

  // sort piedata according to match tickers
  pieData.sort((a, b) => {
    return tickers.indexOf(a.name) - tickers.indexOf(b.name)
  })
  targetPieData.sort((a, b) => {
    return tickers.indexOf(a.name) - tickers.indexOf(b.name)
  })

  const deviationThreshold = agentInfo?.["Deviation-Threshold"]
    ? (Number.parseInt(agentInfo?.["Deviation-Threshold"]) / 100).toFixed(2) + "%"
    : 0

  const highestImbalance = useMemo(() => {
    let val = 0

    for (const entry of agentInfo?.Holdings?.entries || []) {
      if (entry.realDeviation > val) {
        val = entry.realDeviation
      }
    }

    return (val / 100).toFixed(2) + "%"
  }, [agentInfo])

  const lastRebalance =
    agentInfo && agentInfo.Rebalancings && agentInfo.Rebalancings?.length > 0
      ? agentInfo.Rebalancings[agentInfo.Rebalancings?.length - 1].completedAt
      : 0

  const [togglePauseLoading, setTogglePauseLoading] = useState(false)

  const handleTogglePause = useCallback(async () => {
    setTogglePauseLoading(true)
    try {
      await togglePause(agent.id)
      await fetchAgentInfo()
    } catch (e) {
      console.error("Failed to toggle agent paused", e)
    }
    setTogglePauseLoading(false)
  }, [agent.id, fetchAgentInfo])

  const activeAddress = useActiveAddress()
  const showActions = activeAddress === agent.portfolio_owner && agentInfo
  const isPaused = agentInfo?.Status === "Paused"
  const needsTopup = agentInfo?.Status === "AwaitingInitialDeposit"

  const cannotPause =
    agentInfo?.Status &&
    [
      "Deactivated",
      "Retired",
      "EmergencyExited",
      // "Rebalancing",
      "Liquidating",
      "EmergencyExiting",
      "AwaitingSubscriptions",
      "AwaitingStrategyConfiguration",
    ].includes(agentInfo.Status)

  const cannotWithdraw =
    (agentInfo?.Status &&
      ["Retired", "Withdrawing", "EmergencyExited", "EmergencyExiting"].includes(
        agentInfo.Status,
      )) ||
    agentInfo?.["Has-Pending-Withdrawal"] === "true"

  const cannotTopup =
    agentInfo?.Status &&
    ["Retired", "EmergencyExiting", "EmergencyExited", "Rebalancing"].includes(agentInfo.Status)

  let pauseButtonText
  if (isPaused) {
    pauseButtonText = togglePauseLoading ? "Resuming..." : "Resume"
  } else {
    pauseButtonText = togglePauseLoading ? "Pausing..." : "Pause"
  }

  const [withdrawalLoading, setWithdrawalLoading] = useState(false)

  const [openWithdrawDialog, setOpenWithdrawDialog] = useState(false)

  const handleOpenWithdrawDialog = useCallback(() => {
    setOpenWithdrawDialog(true)
  }, [])

  const rebalancingDuration = useMemo(() => {
    if (!agentInfo) return false
    if (agentInfo.Status !== "Rebalancing") return false
    if (!agentInfo.CurrentRebalancing) return false

    const duration = (Date.now() - agentInfo.CurrentRebalancing.startedAt) / 1000 / 60
    console.log("onRebalancingDuration(m)", duration)

    return duration
  }, [agentInfo, lastRebalance])

  const rebalancingStuck = rebalancingDuration && rebalancingDuration > 5

  const handleConfirmWithdraw = useCallback(async () => {
    setWithdrawalLoading(true)
    if (!rebalancingStuck && !needsTopup) {
      try {
        await withdraw(agent.id)
        await fetchAgentInfo()
      } catch (e) {
        console.error("Failed to withdraw from agent", e)
      }
    } else {
      try {
        await emergencyExit(agent.id)
        await fetchAgentInfo()
      } catch (e) {
        console.error("Failed to emergency exit agent", e)
      }
    }
    setWithdrawalLoading(false)
  }, [agent.id, fetchAgentInfo, rebalancingStuck])

  const withdrawButtonText = cannotWithdraw
    ? "Withdrawal Requested"
    : needsTopup
      ? "Archive"
      : withdrawalLoading
        ? "Withdrawing..."
        : "Withdraw"

  const emergencyExitButtonText = cannotWithdraw
    ? "Emergency Exit Requested"
    : withdrawalLoading
      ? "Emergency Exiting..."
      : "Emergency Exit"

  const emergencyExitButtonTooltip = needsTopup
    ? "Store this inactive agent away so it’s no longer visible or active."
    : "Stop the agent and transfer assets back to your account."

  const hasUnbalancedInfo = typeof agentInfo?.Holdings?.isUnbalanced === "boolean"
  const isUnbalanced = hasUnbalancedInfo && agentInfo?.Holdings.isUnbalanced

  const isSlippageIgnored = agentInfo?.["Slippage-Tolerance"] === "n/A"

  const [showDetails, setShowDetails] = useState(false)

  const actionsElement = showActions ? (
    <Stack direction="row" gap={2} alignItems={"center"}>
      {quoteTokenInfo && activeAddress && showActions && (
        <DepositForm
          cannotTopup={!!cannotTopup}
          activeAddress={activeAddress}
          ticker={quoteTokenInfo.ticker}
          denomination={quoteTokenInfo.denomination}
          quoteTokenAddress={quoteTokenInfo.id}
          agentId={agent.id}
          refetch={fetchAgentInfo}
          agentInfo={agentInfo}
        />
      )}

      <Box sx={{ color: "accent.main" }}>
        <Button
          onClick={handleTogglePause}
          disabled={cannotPause || togglePauseLoading}
          variant="outlined"
          color="inherit"
          sx={{
            height: 40,
            width: 100,
            textTransform: "none",
          }}
        >
          <Typography variant="body2" component="span" color="inherit" fontWeight={700}>
            {pauseButtonText}
          </Typography>
        </Button>
      </Box>

      {agentInfo && !cannotWithdraw && (
        <Box sx={{ color: "rgba(0, 89, 255, 1)" }}>
          <MuiTooltip title={emergencyExitButtonTooltip}>
            <Button
              onClick={handleOpenWithdrawDialog}
              disabled={withdrawalLoading}
              variant="outlined"
              color="inherit"
              sx={{ height: 40, width: 100, textTransform: "none" }}
            >
              <Typography variant="body2" component="span" fontWeight={700}>
                {rebalancingStuck ? emergencyExitButtonText : withdrawButtonText}
              </Typography>
            </Button>
          </MuiTooltip>
        </Box>
      )}

      <Dialog
        open={openWithdrawDialog}
        onClose={() => setOpenWithdrawDialog(false)}
        sx={{
          " & .MuiDialog-paper": {
            minWidth: 700,
          },
        }}
      >
        <DialogTitle>
          <Typography
            variant="h3"
            sx={{
              textTransform: "uppercase",
              fontSize: "2rem",
              // color: "rgb(255, 195, 254)",
            }}
            fontFamily={NumberFont}
            fontWeight={700}
          >
            Wind down Portfolio Agent
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            This action will transfer all of the agent's asset to you and then stop the agent,
            archiving it. You will still be able to see his history.
          </Typography>
          {!agentInfo ? (
            <LoadingSkeletons />
          ) : (
            <>
              <Box>
                <Typography
                  sx={{
                    marginTop: 2,
                    marginBottom: 1,
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                  variant="h4"
                >
                  Withdrawing{" "}
                  {typeof netWorth === "number" && (
                    <Typography fontFamily={NumberFont} variant="inherit" component="span">
                      <UsdAmountBlock amount={netWorth} />
                    </Typography>
                  )}
                </Typography>

                <Box>
                  <InMemoryTable
                    initialSortField="value"
                    initialSortDir="desc"
                    data={agentInfo.Holdings?.entries}
                    headerCells={[
                      { field: "tokenId", label: "Token" },
                      { label: "Amount", align: "right" },
                      // { label: "Price", align: "right" },
                      { label: "Total Value", align: "right", field: "value" },
                    ]}
                    renderRow={(row: HoldingEntry) => (
                      <TableRow
                        key={row.token}
                        sx={{
                          background: "var(--mui-palette-paperAlt)",
                        }}
                      >
                        <TableCell>
                          <Stack
                            direction="row"
                            alignItems="center"
                            gap={1}
                            sx={{
                              fontSize: "1rem",
                              fontWeight: 700,
                            }}
                          >
                            <TokenAvatar tokenId={row.token} />
                            <IdBlock
                              label={row.ticker || truncateId(row.token)}
                              value={row.token}
                              href={`/token/${row.token}`}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell align="right" variant="number">
                          <TokenAmountBlock
                            amount={row.amount}
                            tokenId={row.token}
                            hideCopyToClipboard
                            needsParsing
                          />
                        </TableCell>
                        <TableCell align="right" variant="number">
                          {row.realNetValue !== undefined && tokenPrices && (
                            // <UsdAmountBlock amount={row.realNetValueUSD} />
                            <UsdAmountBlock
                              amount={
                                Number(
                                  parseBigIntAsNumber(
                                    row.realNetValue,
                                    quoteTokenInfo?.denomination,
                                  ),
                                ) * tokenPrices[agent.quote_token].price
                              }
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  />
                </Box>

                <Typography
                  sx={{
                    marginTop: 2,
                    marginBottom: 1,
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                  variant="h4"
                >
                  Deposit History
                </Typography>
                <Box>
                  <InMemoryTable
                    initialSortField="timestamp"
                    initialSortDir="desc"
                    data={agentInfo.Deposits}
                    headerCells={[
                      { label: "Date", field: "timestamp", align: "right" },
                      { label: "Amount", align: "right" },
                      { label: "Value (USD)", align: "right" },
                    ]}
                    renderRow={(deposit: any) => (
                      <TableRow
                        key={deposit.timestamp}
                        sx={{
                          background: "var(--mui-palette-paperAlt)",
                        }}
                      >
                        <TableCell align="right" variant="number">
                          {formatFullDate(deposit.timestamp)}
                        </TableCell>
                        <TableCell align="right" variant="number">
                          <TokenAmountBlock
                            amount={deposit.amount}
                            tokenId={agent.quote_token}
                            hideCopyToClipboard
                            needsParsing
                          />
                          {quoteTokenInfo?.ticker}
                        </TableCell>
                        <TableCell align="right" variant="number">
                          <UsdAmountBlock
                            amount={parseBigIntAsNumber(
                              deposit.usdValue,
                              quoteTokenInfo?.denomination,
                            )}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  />
                </Box>

                <Typography
                  sx={{
                    marginTop: 2,
                    marginBottom: 1,
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                  variant="h4"
                >
                  Agent Summary
                </Typography>
                <ul>
                  <Typography component="li">
                    Agent {agentInfo["Agent-Name"]} has been running for{" "}
                    {formatDuration(Date.now() - agent.created_at_ts, "milliseconds")}, rebalancing{" "}
                    {(agentInfo.Rebalancings || [])?.length}{" "}
                    {(agentInfo.Rebalancings || []).length === 1 ? "time" : "times"}.
                  </Typography>
                  {agentExitPnl && (
                    <Typography component="li">
                      Over its lifetime you deposited{" "}
                      <b>
                        <UsdAmountBlock amount={totalDeposited || "0"} />
                      </b>
                      , netting a {pnl && pnl >= 0 ? "profit" : "loss"} of{" "}
                      <b
                        style={{
                          color:
                            pnl && pnl >= 0
                              ? "var(--mui-palette-success-main)"
                              : pnl === 0
                                ? undefined
                                : "var(--mui-palette-error-main)",
                        }}
                      >
                        <UsdAmountBlock amount={Math.abs(pnl || 0)} />(
                        {Number(agentExitPnl?.relPnlAllTime) / 100}%)
                      </b>
                      .
                    </Typography>
                  )}
                </ul>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenWithdrawDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmWithdraw}
            disabled={cannotWithdraw || withdrawalLoading}
            color="primary"
            autoFocus
            variant="outlined"
          >
            {rebalancingStuck ? emergencyExitButtonText : withdrawButtonText}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  ) : null

  const allocationElement = (
    <Stack
      alignItems="center"
      sx={{ overflow: "hidden" }}
      direction="row"
      gap={2}
      justifyContent="center"
    >
      {targetPieData && targetPieData.length > 0 && (
        <Stack
          alignItems="center"
          sx={{
            "& .recharts-text.recharts-pie-label-text": {
              fontSize: "0.75rem",
            },
          }}
          gap={2}
        >
          <Typography variant="caption" color="secondary">
            {" "}
            Target{" "}
          </Typography>
          <PieChart width={120} height={120}>
            <Pie
              data={targetPieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={40}
              strokeWidth={2}
              stroke="var(--mui-palette-paperAlt)"
              opacity={0.8}
            >
              {targetPieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ fontSize: "0.75rem" }}
              separator=": "
              formatter={(value) => `${Number(value).toFixed(2)}%`}
            />
          </PieChart>
        </Stack>
      )}
      {pieData && pieData.length > 0 && (
        <Stack
          alignItems="center"
          sx={{
            "& .recharts-text.recharts-pie-label-text": {
              fontSize: "0.75rem",
            },
          }}
          gap={2}
        >
          <Typography variant="caption" color="secondary">
            {" "}
            Current{" "}
          </Typography>
          <PieChart width={120} height={120}>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={60}
              strokeWidth={2}
              stroke="var(--mui-palette-paperAlt)"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ fontSize: "0.75rem" }}
              separator=": "
              formatter={(value) => `${Number(value).toFixed(2)}%`}
            />
          </PieChart>
        </Stack>
      )}

      {hasUnbalancedInfo && pieData.length > 0 && (
        <Typography
          variant="subtitle1"
          marginTop={3.5}
          color={
            isUnbalanced ? "var(--mui-palette-warning-main)" : "var(--mui-palette-success-main)"
          }
        >
          {isUnbalanced ? "Unbalanced" : "Balanced"}
        </Typography>
      )}
    </Stack>
  )

  const detailsElement = (
    <>
      <Paper
        sx={{
          border: "1px solid var(--mui-palette-divider)",
          flexGrow: 1,
          paddingX: 2,
          paddingY: 1,
        }}
      >
        <Stack>
          <Typography variant="caption" color="secondary">
            Quote Token
          </Typography>
          <Typography fontFamily={NumberFont}>{quoteTokenInfo?.ticker}</Typography>
        </Stack>
      </Paper>
      <Paper
        sx={{
          border: "1px solid var(--mui-palette-divider)",
          flexGrow: 1,
          paddingX: 2,
          paddingY: 1,
        }}
      >
        <Stack>
          <Typography variant="caption" color="secondary">
            Created
          </Typography>
          <Typography fontFamily={NumberFont}>{formatRelative(agent.created_at_ts)}</Typography>
        </Stack>
      </Paper>
      {agentInfo ? (
        <Paper
          sx={{
            border: "1px solid var(--mui-palette-divider)",
            flexGrow: 1,
            paddingX: 2,
            paddingY: 1,
          }}
        >
          <Stack>
            <Typography variant="caption" color="secondary">
              Max Slippage
            </Typography>
            <Typography fontFamily={NumberFont}>
              {isSlippageIgnored ? (
                <Typography variant="inherit" color="var(--mui-palette-warning-main)">
                  Flexible
                </Typography>
              ) : (
                (Number.parseInt(agentInfo!["Slippage-Tolerance"]) / 100).toFixed(2) + "%"
              )}
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <Skeleton width={80} sx={{ display: "inline-block" }} />
      )}
      {agentExitPnl !== undefined && (
        <Paper
          sx={{
            border: "1px solid var(--mui-palette-divider)",
            flexGrow: 1,
            paddingX: 2,
            paddingY: 1,
          }}
        >
          <Stack>
            <Typography variant="caption" color="secondary">
              PnL
            </Typography>

            <Typography
              color="accent.main"
              fontFamily={NumberFont}
              fontWeight={400}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                color:
                  pnl && pnl >= 0
                    ? "var(--mui-palette-success-main)"
                    : pnl === 0
                      ? undefined
                      : "var(--mui-palette-error-main)",
              }}
            >
              <UsdAmountBlock amount={Math.abs(pnl || 0)} /> (
              {Number(agentExitPnl?.relPnlAllTime) / 100}%)
            </Typography>
          </Stack>
        </Paper>
      )}
    </>
  )

  const rebalanceZoneEl = (
    <>
      {deviationThreshold !== 0 ? (
        <Stack
          direction={!singleAgentView ? "column" : "row"}
          justifyContent={"space-between"}
          alignContent={"center"}
        >
          <Typography fontFamily={NumberFont} fontWeight={700}>
            {pieData.length > 0 && (
              <>
                <Typography
                  variant="inherit"
                  component="span"
                  color={
                    isUnbalanced
                      ? "var(--mui-palette-warning-main)"
                      : "var(--mui-palette-success-main)"
                  }
                >
                  {highestImbalance}
                </Typography>{" "}
                of{" "}
              </>
            )}
            <Typography variant="inherit" component="span">
              {deviationThreshold}
            </Typography>
          </Typography>
          <Typography
            variant="caption"
            color="secondary"
            fontFamily={NumberFont}
            fontWeight={100}
            fontSize={10}
          >
            Last rebalance {formatRelative(lastRebalance)}
          </Typography>
        </Stack>
      ) : (
        <Skeleton width={80} sx={{ display: "inline-block" }} />
      )}
    </>
  )

  const statusEl = (
    <>
      {agentInfo ? (
        <Stack
          direction="row"
          gap={0.5}
          alignContent={"center"}
          sx={{
            "& .dexi-spinner": {
              animation: "spin 1.5s linear infinite",
              "@keyframes spin": {
                from: {
                  transform: "rotate(0deg)",
                },
                to: {
                  transform: "rotate(360deg)",
                },
              },
            },
          }}
        >
          <Typography variant="body1" fontWeight={700} color={STATUS_COLOR_MAP[agentInfo.Status]}>
            {agentInfo.Status === "Ready"
              ? isUnbalanced
                ? "Waiting for market update"
                : "Balanced"
              : STATUS_LABEL_MAP[agentInfo.Status]}
          </Typography>
          {agentInfo["Is-Paused-After-High-Slippage"] === "true" && (
            <MuiTooltip title="This Portfolio Agent has been paused due to high slippage after a rebalancing.">
              <InfoOutlined
                fontSize="small"
                sx={{
                  color: STATUS_COLOR_MAP[agentInfo.Status],
                }}
              />
            </MuiTooltip>
          )}
          {PENDING_STATUSES.includes(agentInfo.Status) && (
            <DexiSpinner color={STATUS_COLOR_MAP[agentInfo.Status]} />
          )}
        </Stack>
      ) : (
        <Skeleton width={80} sx={{ display: "inline-block" }} />
      )}
    </>
  )

  if (singleAgentView) {
    return (
      <Stack gap={6}>
        <Box
          marginTop={2}
          sx={{
            "& > div > *": { width: "100%" },
            "& > div > div > *": { width: "100%" },
          }}
        >
          {actionsElement}
        </Box>
        <Box>
          <Stack direction="row" gap={2} justifyContent={"space-between"}>
            <Paper
              sx={{
                border: "1px solid var(--mui-palette-divider)",
                flexGrow: 1,
                paddingX: 2,
                paddingY: 1,
              }}
            >
              <Stack>
                <Typography variant="caption" color="secondary" component={"div"}>
                  <Stack direction="row" gap={0.5} alignItems="center" justifyContent="flex-start">
                    Status
                    {rebalancingStuck && (
                      <MuiTooltip
                        title={`Something went wrong, please exit this portfolio agent. It seems to be stuck rebalancing for ${formatDuration(rebalancingDuration)}.`}
                      >
                        <InfoOutlined
                          fontSize="small"
                          sx={{ fontSize: "1rem", color: "var(--mui-palette-warning-main)" }}
                        />
                      </MuiTooltip>
                    )}
                  </Stack>
                </Typography>
                {statusEl}
              </Stack>
            </Paper>
            <Paper
              sx={{
                border: "1px solid var(--mui-palette-divider)",
                flexGrow: 1,
                paddingX: 2,
                paddingY: 1,
              }}
            >
              <Stack>
                <Typography variant="caption" color="secondary" component={"div"}>
                  <Stack direction="row" gap={0.5} alignItems="center" justifyContent="flex-start">
                    Current max. imbalance
                    <MuiTooltip title="Displays the largest deviation from the target allocation currently present in your portfolio. This percentage is relative to each token’s target share, rather than the entire portfolio.">
                      <InfoOutlined fontSize="small" sx={{ fontSize: "1rem" }} />
                    </MuiTooltip>
                  </Stack>
                </Typography>
                {rebalanceZoneEl}
              </Stack>
            </Paper>
            {detailsElement}
          </Stack>
        </Box>
        <Stack direction="row" gap={6} alignItems="stretch">
          {chartData.length > 0 && (
            <Stack flexGrow={1}>
              <Paper sx={{ border: 0, paddingX: 2, paddingY: 2 }}>
                <Typography variant="caption" color="secondary">
                  Historical Networth
                </Typography>
                <Box sx={{ width: "100%", height: 240 }}>
                  <ResponsiveContainer>
                    <AreaChart data={chartData}>
                      <defs>
                        {tickers.map((ticker, index) => (
                          <linearGradient
                            key={ticker}
                            id={`color${ticker}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor={COLORS[index % COLORS.length]}
                              stopOpacity={0.8}
                            />
                            <stop
                              offset="95%"
                              stopColor={COLORS[index % COLORS.length]}
                              stopOpacity={0}
                            />
                          </linearGradient>
                        ))}
                      </defs>
                      <XAxis
                        dataKey="timestamp"
                        tickFormatter={formatDate}
                        domain={["dataMin", "dataMax"]}
                        type="category"
                        hide
                      />
                      <YAxis
                        orientation="right"
                        tickFormatter={(value) => `$${formatNumberAuto(value)}`}
                        domain={["auto", "auto"]}
                        hide
                      />
                      <Tooltip content={CustomTooltip} />
                      {tickers.map((ticker, index) => (
                        <Area
                          key={ticker}
                          // type="monotone"
                          type="linear"
                          dataKey={ticker}
                          stackId="1"
                          stroke={COLORS[index % COLORS.length]}
                          fillOpacity={1}
                          fill={`url(#color${ticker})`}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Stack>
          )}
          {pieData && pieData.length > 0 && (
            <Paper sx={{ border: 0, paddingX: 2, paddingY: 2 }}>
              <Stack gap={4}>
                <Typography variant="caption" color="secondary">
                  Allocation
                </Typography>
                {allocationElement}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Stack>
    )
  }

  const expandButton = (
    <Button
      onClick={() => {
        setShowDetails(!showDetails)
      }}
      size="small"
      sx={{
        background: (theme) => (needsTopup ? "" : theme.palette.action.hover),
        minWidth: "unset",
        width: 50,
        paddingY: 0,
        // height: 16,
      }}
    >
      {showDetails ? (
        <KeyboardArrowUp fontSize={"small"} />
      ) : (
        <KeyboardArrowDown fontSize={"small"} />
      )}
    </Button>
  )

  return (
    <>
      <TableRow
        style={{
          background: !showDetails
            ? "var(--mui-palette-paperAlt)"
            : "var(--mui-palette-paperAltSelected",
        }}
        sx={{
          transition: "background 0.3s",
          fontSize: "1rem",
          fontWeight: 700,
          "& > td": { border: 0, paddingTop: 2 },
        }}
        onClick={() => {
          setShowDetails(!showDetails)
        }}
      >
        <TableCell align="right" variant="head">
          {index + 1}
        </TableCell>
        <TableCell>
          <Typography variant="body1" fontWeight={700} marginTop={2}>
            <IdBlock
              label={agentInfo?.["Agent-Name"] || truncateId(agent.id)}
              value={agent.id}
              // href={`/entity/${agent.id}`}
            />
            <Chip
              to={`/entity/${agent.id}`}
              sx={{
                borderRadius: 0.5,
                paddingY: 0.25,
                height: "fit-content",
              }}
              label={<Typography variant="caption">View on Agent Page</Typography>}
              component={Link}
              onClick={(event) => {
                event.stopPropagation()
              }}
            />
          </Typography>
        </TableCell>
        <TableCell>
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={-2}
            sx={{
              "& > div > *": {
                border: (theme) => `2px solid ${theme.palette.paperAlt}`,
              },
            }}
          >
            {agent.token_processes.map((token) => (
              <Token key={token} tokenId={token} />
            ))}
          </Stack>
        </TableCell>
        <TableCell align="right">{statusEl}</TableCell>
        <TableCell align="right">
          {netWorth !== null ? (
            <Typography
              // color="accent.main"
              fontFamily={NumberFont}
              fontWeight={700}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
            >
              <UsdAmountBlock amount={netWorth} />
            </Typography>
          ) : (
            <Skeleton width={80} sx={{ display: "inline-block" }} />
          )}
        </TableCell>
        <TableCell align="right">
          <Box marginTop={2}>{rebalanceZoneEl}</Box>
        </TableCell>
        <TableCell align="right">
          <ResponsiveContainer width="100%" height={50}>
            <LineChart
              // TODO
              data={last7DaysData}
              // data={[
              //   { name: 1733949009441, value: 100 },
              //   { name: 1733949009441 + 1 * 86400 * 1000, value: 200 },
              //   { name: 1733949009441 + 2 * 86400 * 1000, value: 300 },
              //   { name: 1733949009441 + 3 * 86400 * 1000, value: 100 },
              //   { name: 1733949009441 + 4 * 86400 * 1000, value: 500 },
              //   { name: 1733949009441 + 5 * 86400 * 1000, value: 800 },
              // ]}
              margin={{ left: 40, top: 5, bottom: 5 }}
            >
              <XAxis dataKey="name" hide />
              <YAxis hide />
              <Line
                type="bump"
                stroke="var(--mui-palette-accent-main)"
                dataKey="value"
                strokeWidth={2}
                dot={false}
              />
              <Tooltip
                contentStyle={{ fontSize: "0.75rem" }}
                separator=""
                itemStyle={{ color: "black" }}
                // labelFormatter={(x) => formatDate(x)}
                labelFormatter={() => ""}
                formatter={(value) => [`$${formatNumberAuto(String(value), 2)}`, ""]}
              />
            </LineChart>
          </ResponsiveContainer>
        </TableCell>
      </TableRow>

      <TableRow
        sx={{
          fontSize: "1rem",
          fontWeight: 700,
          "&:last-of-type > td": {
            borderBottomRightRadius: 15,
            borderBottomLeftRadius: 15,
          },
        }}
      >
        <TableCell
          colSpan={9}
          style={{
            background: !showDetails
              ? "var(--mui-palette-paperAlt)"
              : "var(--mui-palette-paperAltSelected",
          }}
          sx={{
            background: "var(--mui-palette-paperAlt)",
            transition: "background 0.3s",
          }}
        >
          <Stack alignItems="center" marginBottom={1}>
            {needsTopup ? (
              <ShineBorder
                color="var(--mui-palette-accent-main)"
                bgColor="var(--mui-palette-background-paper)"
              >
                {expandButton}
              </ShineBorder>
            ) : (
              expandButton
            )}
          </Stack>
          <Collapse in={showDetails}>
            <Box
              sx={{
                overflow: "hidden",
                borderRadius: 1,
                background: "var(--mui-palette-paperAlt)",
                marginBottom: 1,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr 1fr",
                  alignItems: "flex-start",
                  gap: 1,
                  paddingX: 10,
                  paddingY: 2,
                }}
              >
                <Stack gap={4}>
                  <div>
                    <Stack>
                      <Typography variant="body2">Total Networth</Typography>
                      <Stack direction="row" gap={2} alignItems="center">
                        {netWorth !== null ? (
                          <Typography
                            color="accent.main"
                            fontFamily={NumberFont}
                            fontWeight={400}
                            variant="h3"
                            sx={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                          >
                            <UsdAmountBlock amount={netWorth} />
                          </Typography>
                        ) : (
                          <Skeleton width={80} sx={{ display: "inline-block" }} />
                        )}
                        <Stack direction="row" gap={2}>
                          {detailsElement}
                        </Stack>
                      </Stack>
                    </Stack>
                  </div>
                  {actionsElement}
                </Stack>
                {allocationElement}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export function Token(props: { tokenId: string }) {
  const { tokenId } = props

  const [tokenInfo] = useTokenInfo(tokenId)

  return (
    <MuiTooltip title={tokenInfo?.ticker || tokenInfo?.name || tokenId}>
      <div>
        <TokenAvatar tokenId={tokenId} size="large" />
      </div>
    </MuiTooltip>
  )
}

const CustomTooltip: React.FC<TooltipProps<number, string>> = (props) => {
  const { active, payload, label } = props
  const theme = useTheme()

  if (active && payload && payload.length) {
    // Calculate total value by summing up the values of all assets
    const totalValue = payload.reduce((acc, curr) => acc + (curr.value || 0), 0)

    // Format the timestamp label
    const formattedDate = formatFullDate(label as number)

    return (
      <div
        style={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          padding: "10px",
          borderRadius: "4px",
          color: theme.palette.text.primary,
        }}
      >
        <p style={{ margin: 0, fontWeight: "bold" }}>{formattedDate}</p>
        <p style={{ margin: "5px 0", fontWeight: "bold" }}>Total Value: ${totalValue.toFixed(2)}</p>
        <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
          {payload.map((entry, index) => (
            <li key={`item-${index}`} style={{ color: entry.color || theme.palette.text.primary }}>
              {entry.name}: ${entry.value?.toFixed(2)}
            </li>
          ))}
          {payload?.[0].payload.numDeposits && (
            <li>Deposits: {payload?.[0].payload.numDeposits}</li>
          )}
          {payload?.[0].payload.numRebalancings && (
            <li>Rebalancings: {payload?.[0].payload.numRebalancings}</li>
          )}
        </ul>
      </div>
    )
  }

  return null
}
